/**
 * FarmCo Marketplace Search Engine
 * Unified search across products, farms and postcodes
 * Drop into theme as a snippet and render with <script> tag
 * v6
 */

(function () {
  'use strict';

  var STOREFRONT_TOKEN = '0e62a151b9842ccde0203ac8adb40345';
  var SHOP_DOMAIN      = 'farmcowales.myshopify.com';
  var STOREFRONT_URL   = 'https://' + SHOP_DOMAIN + '/api/2026-01/graphql.json';

  // ── Postcode detection ──────────────────────────────────────
  function looksLikePostcode(v) {
    return /^[A-Z]{1,2}[0-9][A-Z0-9]?(\s?[0-9][A-Z]{2})?$/i.test(v.trim().replace(/\s+/g,' '));
  }

  function normalisePostcode(v) {
    return v.trim().replace(/\s+/g,'').toUpperCase();
  }

  function isFullPostcode(v) {
    return /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/i.test(v.replace(/\s/g,''));
  }

  // ── Haversine distance ──────────────────────────────────────
  function haversine(lat1, lng1, lat2, lng2) {
    var R = 3958.8; // miles
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLng = (lng2 - lng1) * Math.PI / 180;
    var a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  // ── Storefront GraphQL ──────────────────────────────────────
  function gql(query, variables) {
    return fetch(STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function(r) { return r.json(); });
  }

  // ── Product search ──────────────────────────────────────────
  var PRODUCT_QUERY = `
    query ProductSearch($query: String!) {
      products(first: 6, query: $query, sortKey: RELEVANCE) {
        edges {
          node {
            id
            title
            handle
            vendor
            featuredImage { url(transform: {maxWidth: 120, maxHeight: 120, crop: CENTER}) alt }
            priceRange { minVariantPrice { amount currencyCode } }
            variants(first: 1) {
              edges { node { id availableForSale } }
            }
          }
        }
      }
    }
  `;

  // ── Farm/metaobject search ──────────────────────────────────
  var FARM_QUERY = `
    query FarmSearch {
      metaobjects(type: "farm_name", first: 50) {
        edges {
          node {
            id
            fields {
              key
              value
              reference {
                ... on MediaImage {
                  image { url(transform: {maxWidth: 80, maxHeight: 80, crop: CENTER}) }
                }
              }
            }
          }
        }
      }
    }
  `;

  // ── Parse farm fields ───────────────────────────────────────
  function parseFarm(node) {
    var f = {};
    node.fields.forEach(function(field) {
      if (field.key === 'image' && field.reference && field.reference.image) {
        f.image = field.reference.image.url;
      } else {
        f[field.key] = field.value;
      }
    });
    return f;
  }

  // ── Match farms to query ────────────────────────────────────
  function matchFarms(farms, query) {
    var q = query.toLowerCase().trim();
    return farms.filter(function(f) {
      var haystack = [
        f.farm_name, f.location, f.postcode,
        f.pick_up_location, f.producer_type,
        f.vendor_tag, f.delivery_notes
      ].join(' ').toLowerCase();
      return haystack.indexOf(q) !== -1;
    }).slice(0, 3);
  }

  // ── Category suggestions ────────────────────────────────────
  var CATEGORY_MAP = {
    'beef':     { label: 'The Butcher', type: 'butcher', icon: '🥩' },
    'lamb':     { label: 'The Butcher', type: 'butcher', icon: '🥩' },
    'pork':     { label: 'The Butcher', type: 'butcher', icon: '🥩' },
    'chicken':  { label: 'The Butcher', type: 'butcher', icon: '🥩' },
    'meat':     { label: 'The Butcher', type: 'butcher', icon: '🥩' },
    'mince':    { label: 'The Butcher', type: 'butcher', icon: '🥩' },
    'steak':    { label: 'The Butcher', type: 'butcher', icon: '🥩' },
    'sausage':  { label: 'The Butcher', type: 'butcher', icon: '🥩' },
    'veg':      { label: 'The Grocer',  type: 'grocer',  icon: '🥦' },
    'vegetable':{ label: 'The Grocer',  type: 'grocer',  icon: '🥦' },
    'fruit':    { label: 'The Grocer',  type: 'grocer',  icon: '🍎' },
    'salad':    { label: 'The Grocer',  type: 'grocer',  icon: '🥗' },
    'organic':  { label: 'The Grocer',  type: 'grocer',  icon: '🌿' },
    'potato':   { label: 'The Grocer',  type: 'grocer',  icon: '🥔' },
    'milk':     { label: 'The Dairy',   type: 'dairy',   icon: '🥛' },
    'cheese':   { label: 'The Dairy',   type: 'dairy',   icon: '🧀' },
    'butter':   { label: 'The Dairy',   type: 'dairy',   icon: '🧈' },
    'yoghurt':  { label: 'The Dairy',   type: 'dairy',   icon: '🫙' },
    'dairy':    { label: 'The Dairy',   type: 'dairy',   icon: '🥛' },
    'eggs':     { label: 'The Grocer',  type: 'grocer',  icon: '🥚' },
    'bread':    { label: 'The Baker',   type: 'baker',   icon: '🍞' },
    'sourdough':{ label: 'The Baker',   type: 'baker',   icon: '🍞' },
    'pastry':   { label: 'The Baker',   type: 'baker',   icon: '🥐' },
    'cake':     { label: 'The Baker',   type: 'baker',   icon: '🎂' },
    'bakery':   { label: 'The Baker',   type: 'baker',   icon: '🍞' },
    'loaf':     { label: 'The Baker',   type: 'baker',   icon: '🍞' },
    'honey':    { label: 'The Pantry',  type: 'pantry',  icon: '🍯' },
    'jam':      { label: 'The Pantry',  type: 'pantry',  icon: '🫙' },
    'chutney':  { label: 'The Pantry',  type: 'pantry',  icon: '🫙' },
    'preserve': { label: 'The Pantry',  type: 'pantry',  icon: '🫙' },
    'sauce':    { label: 'The Pantry',  type: 'pantry',  icon: '🫙' },
    'coffee':   { label: 'The Coffee Shop', type: 'coffee-shop', icon: '☕' },
    'tea':      { label: 'The Coffee Shop', type: 'coffee-shop', icon: '🍵' },
    'kombucha': { label: 'The Drink Cellar', type: 'drink-cellar', icon: '🍶' },
    'juice':    { label: 'The Drink Cellar', type: 'drink-cellar', icon: '🍹' },
    'gin':      { label: 'The Drink Cellar', type: 'drink-cellar', icon: '🍸' },
    'wine':     { label: 'The Drink Cellar', type: 'drink-cellar', icon: '🍷' },
    'fish':     { label: 'The Fishmonger', type: 'fishmonger', icon: '🐟' },
    'seafood':  { label: 'The Fishmonger', type: 'fishmonger', icon: '🦞' },
  };

  function getCategoryMatch(query) {
    var q = query.toLowerCase().trim();
    var seen = {};
    var matches = [];
    Object.keys(CATEGORY_MAP).forEach(function(k) {
      if (q.indexOf(k) !== -1 || k.indexOf(q) !== -1) {
        var cat = CATEGORY_MAP[k];
        if (!seen[cat.type]) {
          seen[cat.type] = true;
          matches.push(cat);
        }
      }
    });
    return matches.slice(0, 3);
  }

  // ── Format price ────────────────────────────────────────────
  function formatPrice(amount, currency) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
      minimumFractionDigits: 2
    }).format(parseFloat(amount));
  }

  // ── Render dropdown ─────────────────────────────────────────
  function renderDropdown(container, data) {
    var html = '<div class="fc-search-dropdown">';
    var hasContent = false;

    // Postcode results
    if (data.postcodeResults && data.postcodeResults.length) {
      hasContent = true;
      html += '<div class="fc-search-section">';
      html += '<div class="fc-search-section-label">FARMS NEAR ' + escHtml(data.postcodeQuery) + '</div>';
      data.postcodeResults.forEach(function(f) {
        var img = f.image
          ? '<img src="' + escHtml(f.image) + '" alt="" class="fc-search-farm-img">'
          : '<div class="fc-search-farm-img fc-search-farm-img--placeholder">' + escHtml((f.farm_name||'?').slice(0,1)) + '</div>';
        var dist = f.distance ? '<span class="fc-search-farm-dist">' + f.distance.toFixed(1) + ' mi</span>' : '';
        var url = f.collection_handle ? '/collections/' + f.collection_handle : '/pages/all-farms';
        html += '<a href="' + url + '" class="fc-search-result fc-search-result--farm">';
        html += '<div class="fc-search-farm-media">' + img + '</div>';
        html += '<div class="fc-search-farm-info">';
        html += '<div class="fc-search-farm-name">' + escHtml(f.farm_name||'') + dist + '</div>';
        if (f.pick_up_location) html += '<div class="fc-search-farm-meta">' + escHtml(f.pick_up_location) + '</div>';
        html += '</div></a>';
      });
      html += '</div>';
    }

    // Product results
    if (data.products && data.products.length) {
      hasContent = true;
      html += '<div class="fc-search-section">';
      html += '<div class="fc-search-section-label">PRODUCTS</div>';
      data.products.forEach(function(p) {
        var img = p.featuredImage
          ? '<img src="' + escHtml(p.featuredImage.url) + '" alt="" class="fc-search-product-img">'
          : '<div class="fc-search-product-img fc-search-product-img--placeholder"></div>';
        var price = p.priceRange ? formatPrice(p.priceRange.minVariantPrice.amount, p.priceRange.minVariantPrice.currencyCode) : '';
        var avail = p.variants.edges[0] && p.variants.edges[0].node.availableForSale;
        html += '<a href="/products/' + escHtml(p.handle) + '" class="fc-search-result fc-search-result--product">';
        html += '<div class="fc-search-product-media">' + img + '</div>';
        html += '<div class="fc-search-product-info">';
        html += '<div class="fc-search-product-title">' + escHtml(p.title) + '</div>';
        html += '<div class="fc-search-product-meta">';
        html += '<span class="fc-search-product-vendor">' + escHtml(p.vendor) + '</span>';
        if (price) html += '<span class="fc-search-product-price">' + price + '</span>';
        if (!avail) html += '<span class="fc-search-product-soldout">Sold out</span>';
        html += '</div></div></a>';
      });
      if (data.query) {
        html += '<a href="/search?type=product&q=' + encodeURIComponent(data.query) + '" class="fc-search-see-all">See all products →</a>';
      }
      html += '</div>';
    }

    // Farm results
    if (data.farms && data.farms.length) {
      hasContent = true;
      html += '<div class="fc-search-section">';
      html += '<div class="fc-search-section-label">FARMS & PRODUCERS</div>';
      data.farms.forEach(function(f) {
        var img = f.image
          ? '<img src="' + escHtml(f.image) + '" alt="" class="fc-search-farm-img">'
          : '<div class="fc-search-farm-img fc-search-farm-img--placeholder">' + escHtml((f.farm_name||'?').slice(0,1)) + '</div>';
        var url = f.collection_handle ? '/collections/' + f.collection_handle : '/pages/all-farms';
        var meta = [f.pick_up_day, f.pick_up_time].filter(Boolean).join(' · ');
        html += '<a href="' + url + '" class="fc-search-result fc-search-result--farm">';
        html += '<div class="fc-search-farm-media">' + img + '</div>';
        html += '<div class="fc-search-farm-info">';
        html += '<div class="fc-search-farm-name">' + escHtml(f.farm_name||'') + '</div>';
        if (f.location) html += '<div class="fc-search-farm-meta">' + escHtml(f.location) + (meta ? ' · ' + meta : '') + '</div>';
        html += '</div></a>';
      });
      html += '</div>';
    }

    // Category suggestions
    if (data.categories && data.categories.length) {
      hasContent = true;
      html += '<div class="fc-search-section">';
      html += '<div class="fc-search-section-label">SHOP BY CATEGORY</div>';
      html += '<div class="fc-search-cats">';
      data.categories.forEach(function(c) {
        html += '<a href="/pages/all-farms?type=' + c.type + '" class="fc-search-cat">' + c.icon + ' ' + escHtml(c.label) + '</a>';
      });
      html += '</div></div>';
    }

    // No results
    if (!hasContent) {
      html += '<div class="fc-search-empty">No results found for <strong>' + escHtml(data.query) + '</strong></div>';
    }

    // View all
    if (hasContent && data.query && !data.postcodeResults) {
      html += '<a href="/pages/all-farms?q=' + encodeURIComponent(data.query) + '" class="fc-search-view-all">View all results for "' + escHtml(data.query) + '" →</a>';
    }

    html += '</div>';
    container.innerHTML = html;
    container.style.display = 'block';
    // Position after display:block so getBoundingClientRect is accurate
    if (container._position) {
      container._position();
    } else {
      // Fallback: find the associated input and position manually
      var associatedInput = document.querySelector('[data-fc-dropdown-id="' + container.id + '"]');
      if (associatedInput) {
        var anchor = associatedInput.closest('.farmco-search-form-wrap') || associatedInput;
        var r = anchor.getBoundingClientRect();
        container.style.top   = (r.bottom + window.scrollY + 8) + 'px';
        container.style.left  = r.left + 'px';
        container.style.width = r.width + 'px';
        container.style.position = 'absolute';
      }
    }
  }

  function escHtml(str) {
    return String(str||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // ── Postcode lookup ─────────────────────────────────────────
  function lookupPostcode(postcode) {
    var url = isFullPostcode(postcode)
      ? 'https://api.postcodes.io/postcodes/' + encodeURIComponent(postcode.replace(/\s/g,''))
      : 'https://api.postcodes.io/outcodes/' + encodeURIComponent(postcode.replace(/\s/g,''));
    return fetch(url).then(function(r) { return r.json(); }).then(function(d) {
      if (d.status === 200) {
        return { lat: d.result.latitude, lng: d.result.longitude };
      }
      return null;
    }).catch(function() { return null; });
  }

  // ── Farm cache ──────────────────────────────────────────────
  var farmCache = null;
  var farmCacheTime = 0;

  function getFarms() {
    var now = Date.now();
    if (farmCache && now - farmCacheTime < 300000) {
      return Promise.resolve(farmCache);
    }
    return gql(FARM_QUERY).then(function(data) {
      if (data.data && data.data.metaobjects) {
        farmCache = data.data.metaobjects.edges.map(function(e) {
          return parseFarm(e.node);
        }).filter(function(f) { return f.farm_name; });
        farmCacheTime = now;
        return farmCache;
      }
      return [];
    }).catch(function() { return []; });
  }

  // ── Main search ─────────────────────────────────────────────
  function doSearch(query, container) {
    var q = query.trim();
    if (q.length < 2) { container.style.display = 'none'; return; }

    var compact = normalisePostcode(q);

    if (looksLikePostcode(compact)) {
      // Postcode search
      Promise.all([lookupPostcode(compact), getFarms()]).then(function(results) {
        var coords   = results[0];
        var farms    = results[1];
        if (!coords) {
          renderDropdown(container, { query: q, products: [], farms: [], categories: [] });
          return;
        }
        var withDist = farms.filter(function(f) {
          return f.postcode && f.postcode.trim();
        }).map(function(f) {
          // Get lat/lng from farm postcode - use cached or approximate
          return f;
        });

        // Look up all farm postcodes
        var lookups = farms.filter(function(f) { return f.postcode; }).map(function(f) {
          return lookupPostcode(f.postcode).then(function(fc) {
            if (fc) {
              f._lat = fc.lat;
              f._lng = fc.lng;
              f.distance = haversine(coords.lat, coords.lng, fc.lat, fc.lng);
            }
            return f;
          });
        });

        Promise.all(lookups).then(function(farmsWithDist) {
          var sorted = farmsWithDist
            .filter(function(f) { return f.distance !== undefined; })
            .sort(function(a,b) { return a.distance - b.distance; })
            .slice(0, 5);
          renderDropdown(container, {
            query: q,
            postcodeQuery: q.toUpperCase(),
            postcodeResults: sorted,
            products: [],
            farms: [],
            categories: []
          });
        });
      });
      return;
    }

    // Text search — run product search and farm match in parallel
    Promise.all([
      gql(PRODUCT_QUERY, { query: q }),
      getFarms()
    ]).then(function(results) {
      var productData = results[0];
      var farms = results[1];

      var products = [];
      if (productData.data && productData.data.products) {
        products = productData.data.products.edges.map(function(e) { return e.node; });
      }

      var matchedFarms = matchFarms(farms, q);
      var categories   = getCategoryMatch(q);

      renderDropdown(container, {
        query: q,
        products: products,
        farms: matchedFarms,
        categories: categories
      });
    }).catch(function() {
      container.style.display = 'none';
    });
  }

  // ── Dropdown styles ─────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('fc-search-styles')) return;
    var style = document.createElement('style');
    style.id = 'fc-search-styles';
    style.textContent = `
      .fc-search-dropdown-wrap {
        position: relative;
        z-index: 9999;
      }
      .fc-search-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        width: min(680px, 96vw);
        background: #fffaf1;
        border: 1px solid rgba(37,37,31,0.12);
        border-radius: 20px;
        box-shadow: 0 16px 48px rgba(37,37,31,0.16);
        overflow: hidden;
        max-height: 80vh;
        overflow-y: auto;
        z-index: 9999;
      }
      .farmco-search-form-wrap .fc-search-dropdown {
        left: 0;
        transform: none;
        width: 100%;
      }
      .fc-search-section {
        padding: 14px 16px 10px;
        border-bottom: 1px solid rgba(37,37,31,0.07);
      }
      .fc-search-section:last-child { border-bottom: none; }
      .fc-search-section-label {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.1em;
        color: rgba(37,37,31,0.45);
        margin-bottom: 8px;
        font-family: Jost, Arial, sans-serif;
      }
      .fc-search-result {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 6px;
        border-radius: 12px;
        text-decoration: none;
        color: #25251F;
        transition: background 0.12s;
      }
      .fc-search-result:hover { background: rgba(37,37,31,0.05); }
      .fc-search-product-img,
      .fc-search-farm-img {
        width: 52px;
        height: 52px;
        border-radius: 10px;
        object-fit: cover;
        flex-shrink: 0;
        background: #eadfce;
      }
      .fc-search-product-img--placeholder,
      .fc-search-farm-img--placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        font-weight: 700;
        color: #fffaf1;
        background: #25251F;
        font-family: Jost, Arial, sans-serif;
      }
      .fc-search-product-title,
      .fc-search-farm-name {
        font-size: 14px;
        font-weight: 600;
        font-family: Jost, Arial, sans-serif;
        line-height: 1.3;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .fc-search-product-meta,
      .fc-search-farm-meta {
        font-size: 12px;
        color: rgba(37,37,31,0.55);
        margin-top: 2px;
        font-family: Jost, Arial, sans-serif;
      }
      .fc-search-product-vendor { margin-right: 8px; }
      .fc-search-product-price {
        font-weight: 700;
        color: #25251F;
      }
      .fc-search-product-soldout {
        font-size: 11px;
        background: rgba(37,37,31,0.08);
        padding: 2px 6px;
        border-radius: 99px;
        color: rgba(37,37,31,0.5);
      }
      .fc-search-farm-dist {
        font-size: 11px;
        font-weight: 600;
        color: #53624B;
        background: rgba(83,98,75,0.1);
        padding: 2px 8px;
        border-radius: 99px;
      }
      .fc-search-cats {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      .fc-search-cat {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 7px 14px;
        border-radius: 99px;
        background: rgba(37,37,31,0.06);
        color: #25251F;
        font-size: 13px;
        font-family: Jost, Arial, sans-serif;
        text-decoration: none;
        transition: background 0.12s;
      }
      .fc-search-cat:hover { background: rgba(37,37,31,0.12); }
      .fc-search-see-all,
      .fc-search-view-all {
        display: block;
        padding: 10px 16px;
        font-size: 13px;
        color: #53624B;
        font-family: Jost, Arial, sans-serif;
        text-decoration: none;
        font-weight: 600;
        border-top: 1px solid rgba(37,37,31,0.07);
      }
      .fc-search-see-all { padding: 8px 6px; border-top: none; }
      .fc-search-view-all:hover,
      .fc-search-see-all:hover { background: rgba(37,37,31,0.04); }
      .fc-search-empty {
        padding: 20px 16px;
        font-size: 14px;
        color: rgba(37,37,31,0.55);
        font-family: Jost, Arial, sans-serif;
        text-align: center;
      }
    `;
    document.head.appendChild(style);
  }

  // ── Wire up a search input ───────────────────────────────────
  function wireInput(input, dropdownContainer) {
    var debounce;
    var lastQuery = '';

    // Remove readonly if present
    input.removeAttribute('readonly');

    input.addEventListener('input', function() {
      clearTimeout(debounce);
      var q = input.value.trim();
      if (q === lastQuery) return;
      lastQuery = q;
      if (q.length < 2) { dropdownContainer.style.display = 'none'; return; }
      debounce = setTimeout(function() { doSearch(q, dropdownContainer); }, 280);
    });

    input.addEventListener('focus', function() {
      if (input.value.trim().length >= 2) doSearch(input.value.trim(), dropdownContainer);
    });

    // Close on outside click
    document.addEventListener('click', function(e) {
      var wrap = input.closest('.farmco-search-form-wrap') || input.closest('.fc-search-dropdown-wrap') || input.closest('form');
      if (wrap && !wrap.contains(e.target)) {
        dropdownContainer.style.display = 'none';
      }
    });

    // Handle submit
    var form = input.closest('form');
    if (form) {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        var q = input.value.trim();
        if (!q) return;
        var compact = normalisePostcode(q);
        if (looksLikePostcode(compact)) {
          window.location.href = '/pages/all-farms?postcode=' + encodeURIComponent(compact);
        } else {
          window.location.href = '/pages/all-farms?q=' + encodeURIComponent(q);
        }
      });
    }
  }

  // ── Create dropdown container ────────────────────────────────
  function createDropdown(input) {
    // Remove any existing dropdown for this input
    var existingId = input.getAttribute('data-fc-dropdown-id');
    if (existingId) {
      var existing = document.getElementById(existingId);
      if (existing) existing.remove();
    }

    // Append to body to escape all overflow:hidden parents
    var div = document.createElement('div');
    var uid = 'fc-drop-' + Math.random().toString(36).slice(2);
    div.id = uid;
    div.className = 'fc-search-dropdown';
    div.style.display = 'none';
    div.style.position = 'fixed';
    div.style.zIndex = '999999';
    document.body.appendChild(div);
    input.setAttribute('data-fc-dropdown-id', uid);

    // Position the dropdown under the input on each show
    function positionDropdown() {
      var anchor = input.closest('.farmco-search-form-wrap') || input;
      var rect = anchor.getBoundingClientRect();
      div.style.top    = (rect.bottom + window.scrollY + 8) + 'px';
      div.style.left   = (rect.left + window.scrollX) + 'px';
      div.style.width  = rect.width + 'px';
      div.style.position = 'absolute';
    }
    div._position = positionDropdown;
    return div;
  }

  // ── Init ─────────────────────────────────────────────────────
  function init() {
    injectStyles();

    // Hero search
    var heroInputs = document.querySelectorAll('[data-fc-search-input]');
    heroInputs.forEach(function(input) {
      var dropdown = createDropdown(input);
      wireInput(input, dropdown);
    });

    // Theme predictive search inputs
    var themeInputs = document.querySelectorAll('input[type="search"], input[name="q"][type="text"]');
    themeInputs.forEach(function(input) {
      if (input.hasAttribute('data-fc-search-input')) return; // already wired
      if (input.closest('[data-fc-search-form]')) return;
      var dropdown = createDropdown(input);
      wireInput(input, dropdown);
    });

    // Prefetch farms on page load
    setTimeout(getFarms, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();